// Split out of src/components/SettingsTab.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only).
// SettingsTab.jsx re-exports everything it exported before, so existing imports keep working.

import { EditPencilIcon, Icon } from "../Common.jsx";
import { CollapsibleSection } from "../ChildView.jsx";
import { InstallButton, useInstallPrompt } from "../Onboarding.jsx";
import { apiConfirmDevice, apiGetDevices, apiRemoveDevice, apiRenameDevice, getDeviceId } from "../../lib/api.js";
import { fmtTime } from "../../lib/utils.js";
import { t as ct } from "../../lib/content.js";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

/* --------------------------------- devices ----------------------------------- */

// Every device that's opened this household, with a "Remove" that kicks one out (Parent
// Mode → Settings → Devices per the 2026-08-16 scoping decision). Loaded on demand when
// this card mounts rather than baked into the ordinary household state, since the list is
// only ever needed while this card is actually open — same reasoning as the passcode reveal
// above. See worker/lib/devices.js for the stored shape and src/lib/api.js's getDeviceId()
// for how this device's own id is generated/persisted.
function DevicesCard({ code, showToast, pinToProve }) {
  const [devices, setDevices] = useState(null); // null while loading
  const [loadError, setLoadError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [confirmingRemoveId, setConfirmingRemoveId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const thisDeviceId = useMemo(() => getDeviceId(), []);

  useEffect(() => {
    let cancelled = false;
    apiGetDevices(code)
      .then((d) => { if (!cancelled) setDevices(d); })
      .catch((err) => { if (!cancelled) setLoadError(err.message || "Couldn't load devices"); });
    return () => { cancelled = true; };
  }, [code]);

  function startEdit(deviceId, current) {
    setEditingId(deviceId);
    setEditValue(current);
  }

  async function saveEdit(deviceId) {
    const label = editValue.trim();
    if (!label) { setEditingId(null); return; }
    setBusyId(deviceId);
    try {
      await apiRenameDevice(code, deviceId, label);
      setDevices((d) => ({ ...d, [deviceId]: { ...d[deviceId], customLabel: label } }));
      showToast(ct("toast.device_renamed", "Device renamed"), "good");
    } catch (err) {
      showToast(err.message || ct("toast.device_rename_failed", "Couldn't rename. Try again"), "bad");
    } finally {
      setBusyId(null);
      setEditingId(null);
    }
  }

  // LB-178: vouch for a device that has never proved it belongs (its token came from the
  // dual-accept auto-mint). The other route to the same standing is a parent entering the
  // PIN on the device itself — this button is for a child's device where that won't happen.
  async function confirmDevice(deviceId) {
    setBusyId(deviceId);
    try {
      const proof = await apiConfirmDevice(code, deviceId, pinToProve);
      setDevices((d) => ({ ...d, [deviceId]: { ...d[deviceId], proven: true, proof } }));
      showToast(ct("toast.device_confirmed", "Device confirmed"), "good");
    } catch (err) {
      showToast(err.message || ct("toast.device_confirm_failed", "Couldn't confirm. Try again"), "bad");
    } finally {
      setBusyId(null);
    }
  }

  async function removeDevice(deviceId) {
    setBusyId(deviceId);
    try {
      await apiRemoveDevice(code, deviceId);
      setDevices((d) => { const next = { ...d }; delete next[deviceId]; return next; });
      setConfirmingRemoveId(null);
      showToast(ct("toast.device_removed", "Device removed"), "good");
    } catch (err) {
      showToast(err.message || ct("toast.device_remove_failed", "Couldn't remove. Try again"), "bad");
    } finally {
      setBusyId(null);
    }
  }

  // Revoked entries are kept server-side (see worker/lib/devices.js) so a removed device's
  // own next request is still recognised — never show them here, they're already gone as
  // far as the parent is concerned.
  const rows = devices
    ? Object.entries(devices)
        .filter(([, d]) => !d.revoked)
        .sort((a, b) => (b[1].lastSeenAt || "").localeCompare(a[1].lastSeenAt || ""))
    : [];

  return (
    <div className="bg-white rounded-lg p-4 border border-slate-200">
      <div className="text-sm font-semibold text-slate-700 mb-1"><Icon id="ic-devices" size={16} className="inline-block align-[-3px] mr-1.5" />{ct("settings.devices_title", "Devices")}</div>
      <p className="text-base text-slate-500 mb-3">Every device that's opened this household. Removing one signs it out — it'll need the code and passcode again to rejoin.</p>

      {devices === null && !loadError && <div className="text-base text-slate-400">{ct("settings.loading", "Loading…")}</div>}
      {loadError && <div className="text-base text-rose-500">{loadError}</div>}
      {devices !== null && rows.length === 0 && <div className="text-base text-slate-400">{ct("settings.no_devices", "No devices recorded yet.")}</div>}

      <div className="flex flex-col gap-2">
        {rows.map(([deviceId, d]) => {
          const isThisDevice = deviceId === thisDeviceId;
          const displayLabel = d.customLabel || d.label;
          const isEditing = editingId === deviceId;
          const isConfirmingRemove = confirmingRemoveId === deviceId;
          return (
            <div key={deviceId} className="border border-slate-100 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  {isEditing ? (
                    <input
                      type="text"
                      autoFocus
                      maxLength={40}
                      className="font-bold text-slate-700 text-sm border-b border-slate-300 focus:outline-none bg-transparent w-full"
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") saveEdit(deviceId); if (e.key === "Escape") setEditingId(null); }}
                    />
                  ) : (
                    <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                      <span className="font-semibold text-slate-700 text-sm truncate">{displayLabel}</span>
                      {isThisDevice && <span className="text-xs font-bold text-emerald-600 bg-emerald-50 rounded px-1.5 py-0.5 flex-shrink-0">{ct("settings.this_device", "This device")}</span>}
                      {/* Which alerts this device is actually registered for. Subscriptions made
                          before device ids were recorded carry no id and so show as silent —
                          slightly pessimistic, but a false "🔔" would be worse than a false "🔕"
                          here, since the whole point of this row is to be trusted. */}
                      <span className={`text-xs font-medium rounded px-1.5 py-0.5 flex-shrink-0 ${d.notifications ? "text-emerald-600 bg-emerald-50" : "text-slate-400 bg-slate-100"}`}>
                        {d.notifications === "parent" ? "Requests" : d.notifications === "child" ? "Replies" : "Silent"}
                      </span>
                      {d.proven === false && (
                        <span className="text-xs font-bold text-amber-700 bg-amber-50 rounded px-1.5 py-0.5 flex-shrink-0">{ct("settings.devices.unconfirmed", "Not yet confirmed")}</span>
                      )}
                    </div>
                  )}
                  <div className="text-xs text-slate-400 mt-0.5">Last seen {fmtTime(d.lastSeenAt)}</div>
                  {d.proven === false && !isEditing && (
                    <div className="text-xs text-slate-500 mt-1">
                      {ct("settings.devices.unconfirmed_hint", "Enter the Parent PIN on it once, or confirm it here.")}{" "}
                      <button className="font-bold text-emerald-600" disabled={busyId === deviceId} onClick={() => confirmDevice(deviceId)}>{ct("settings.devices.confirm", "Confirm")}</button>
                    </div>
                  )}
                </div>

                {isEditing ? (
                  <>
                    <button className="text-xs font-bold text-emerald-600 flex-shrink-0" disabled={busyId === deviceId} onClick={() => saveEdit(deviceId)}>{ct("settings.save_button", "Save")}</button>
                    <button className="text-xs font-bold text-slate-400 flex-shrink-0" onClick={() => setEditingId(null)}>{ct("settings.cancel_button", "Cancel")}</button>
                  </>
                ) : (
                  <button className="flex-shrink-0" title="Rename device" onClick={() => startEdit(deviceId, displayLabel)}>
                    <EditPencilIcon width={13} height={13} />
                  </button>
                )}

                {!isThisDevice && !isEditing && (
                  <button className="text-xs font-bold text-rose-500 flex-shrink-0" onClick={() => setConfirmingRemoveId(deviceId)}>{ct("settings.remove_button", "Remove")}</button>
                )}
              </div>

              {isConfirmingRemove && (
                <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 mt-2">
                  <p className="text-xs text-rose-700 mb-2">This will sign out {displayLabel} — it'll need the household code and passcode to rejoin.</p>
                  <div className="flex gap-2">
                    <button className="flex-1 bg-rose-50 border border-rose-200 text-rose-700 text-xs font-bold rounded-lg py-1.5 disabled:opacity-50" disabled={busyId === deviceId} onClick={() => removeDevice(deviceId)}>{ct("settings.remove_confirm", "Yes, remove")}</button>
                    <button className="flex-1 bg-white border border-slate-200 text-xs font-bold rounded-lg py-1.5" onClick={() => setConfirmingRemoveId(null)}>{ct("settings.cancel_button", "Cancel")}</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// The "Set up the app on this phone" card under Settings → Household. Kept tiny on purpose:
// the whole behaviour lives in InstallButton, which already knows the platform branches and
// the ticket carry. Renders nothing when install is not offerable, rather than promising a
// button that would do nothing.
function SetUpAppCard({ code, open, onToggle }) {
  const { installable } = useInstallPrompt();
  if (!installable) return null;
  return (
    <CollapsibleSection variant="row" icon={<Icon id="ic-install" size={18} />} title={ct("settings.household.card_install", "Set up the app on this phone")} open={open} onToggle={onToggle} testid="settings-install" summary={<p className="text-xs text-slate-500">{ct("settings.household.card_install_sum", "Home screen icon, no passcode needed")}</p>}>
      <div className="pt-1">
        <p className="text-xs text-slate-500 mb-3">{ct("settings.household.install_body", "Put Little Bankers on your home screen and it opens straight into this household — no passcode, nothing to type.")}</p>
        <InstallButton code={code} variant="wide" label={ct("settings.household.install_cta", "Add to home screen")} />
      </div>
    </CollapsibleSection>
  );
}

export { SetUpAppCard, DevicesCard };
